// @ts-check
import React, { useCallback, useRef } from 'react';
import classnames from 'classnames';
import { useIntl, FormattedMessage } from 'react-intl';

import { FloatTextArea } from 'ui-components/components/float-text-area';
import { Text, TextType } from 'ui-components/components/text';

import messages from './messages';
import style from './style.module.less';
import { EventSourceStrings } from '../../../../../../tracking/tracking-constants';
import { useStoreUpdateContext } from '../../context';
import useTrackReturnsSettings from '../hooks/use-track-returns-settings';

/**
 * Email Settings Section component
 * @typedef {object} EmailSettingsSectionProps
 * @property {string} emailMessage - The email message for returns
 * @property {React.ReactNode} [heading] - Heading for the section
 * @property {string} [className] - Additional class name for the section container
 * @returns {React.ReactElement} The Email Settings section
 */
export const EmailSettingsSection = ({
  emailMessage = '',
  heading = <FormattedMessage {...messages.returnEmailMessageHeading} />,
  className = undefined,
}) => {
  const intl = useIntl();
  const trackReturns = useTrackReturnsSettings(EventSourceStrings.STORE_SETTINGS_RETURNS_TAB);
  const { updateStoreSetting } = useStoreUpdateContext();
  const GROUP_NAME = 'returns';

  const emailTextRef = useRef(emailMessage);
  const startModifyingEmailText = useCallback(() => {
    emailTextRef.current = emailMessage;
  }, [emailMessage]);

  const finishModifyingEmailText = useCallback(() => {
    if (emailTextRef.current !== emailMessage) {
      trackReturns.trackUpdateReturnEmailMessage(emailMessage);
    }
  }, [emailMessage, trackReturns]);

  /**
   * @param {string} text
   */
  const updateEmailMessage = (text) => updateStoreSetting(GROUP_NAME, 'emailMessage', text);
  const emailPolicyClassName = classnames(style.emailPolicySection, className);

  const FloatTextAreaTyped = /** @type {any} */ (FloatTextArea);

  return (
    <div className={emailPolicyClassName}>
      <Text className={style.returnSettingsCardHeader} textType={TextType.Label} value={heading} />
      <FloatTextAreaTyped
        className={style.textArea}
        onBlur={finishModifyingEmailText}
        onChange={updateEmailMessage}
        onFocus={startModifyingEmailText}
        placeholder={intl.formatMessage(messages.standardReturnsEmailMessagePlaceholder)}
        value={emailMessage ?? ''}
      />
      <Text
        className={style.returnSettingsDescription}
        textType={TextType.Caption}
        value={messages.standardReturnsEmailMessageDescription}
      />
    </div>
  );
};
